import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios'; // Using axios for consistency

// Import Lucide Icons
import {
    LayoutDashboard, Bot, Mail, // Sidebar
    Menu, Search, Globe, LifeBuoy, Bell, ChevronDown, // Topbar
    BarChart3, // Title Icon / Placeholder
    Activity, // Changed title icon to Activity for variety
    Users, Clock, // Stats Icons
    Download, // Action Icon
    Loader2, // Loading
    ShieldAlert, // Error
    Info, // Info messages
    CheckCircle, // Selected Agent Indicator
    RefreshCw // Regenerate/Loading Icon
} from 'lucide-react';

// --- Animation Utility Hook ---
const useOnScreen = (options = { threshold: 0.1 }) => {
    const ref = useRef();
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        if (typeof IntersectionObserver === 'undefined') { setIsVisible(true); return; }
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setIsVisible(true); /* observer.unobserve(entry.target); */ }
        }, options);
        const currentRef = ref.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef && observer) observer.unobserve(currentRef); };
    }, [options]);
    const hookRef = (node) => { ref.current = node; };
    return [hookRef, isVisible];
};

// --- Styled Clickable Components ---
const ClickableLink = ({ children, className, href = '#', target, rel, onClick, disabled, title, ...props }) => (
    <a href={!disabled ? href : undefined} target={target} rel={target === '_blank' ? 'noopener noreferrer' : rel} onClick={!disabled ? onClick : (e) => e.preventDefault()} role="button" tabIndex={disabled ? -1 : 0} className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${ disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' } ${className}`} aria-disabled={disabled} title={title || (typeof children === 'string' ? children : '')} {...props}>{children}</a>
);
const ClickableDiv = ({ children, className, onClick, disabled, title, ...props }) => (
    <div role="button" tabIndex={disabled ? -1 : 0} onClick={!disabled ? onClick : undefined} onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) onClick?.(e); }} className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${ disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' } ${className}`} aria-disabled={disabled} title={title || (typeof children === 'string' ? children : '')} {...props}>{children}</div>
);

// --- Sidebar Nav Item ---
const SidebarNavItem = ({ icon: Icon, text, href, active, onClick }) => (
     <a href={href} onClick={onClick} className={`flex items-center pl-3 pr-2 py-1.5 rounded-md text-xs font-medium transition-colors duration-150 group relative ${ active ? 'bg-gray-800/60 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800/50' }`}>
         <Icon className={`w-4 h-4 mr-2.5 flex-shrink-0 ${active ? 'text-purple-400' : 'text-gray-500 group-hover:text-gray-300'}`} strokeWidth={1.5} />
         <span className="truncate">{text}</span>
     </a>
);

// --- Sidebar ---
const Sidebar = () => {
    const navigate = useNavigate();
    const { user, isLoaded } = useUser();

    const handleNavigate = (e, path) => {
        e.preventDefault();
        navigate(path);
    };

    const userEmail = isLoaded && user ? user.primaryEmailAddress?.emailAddress : 'Loading...';

    // Determine active path for styling
    const currentPath = window.location.pathname; // Simple way, consider useLocation from react-router-dom for better accuracy

    return (
        <div className="w-52 md:w-56 h-screen bg-black border-r border-gray-800/50 flex flex-col flex-shrink-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black">
            {/* Header */}
            <div className="h-14 flex items-center px-3 flex-shrink-0 border-b border-gray-800/50">
                 <div className="flex items-center">
                     <svg className="w-6 h-6 mr-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#8B5CF6" stroke="#8B5CF6" strokeWidth="1.5"/><path d="M2 17L12 22L22 17" stroke="#8B5CF6" strokeWidth="1.5"/><path d="M2 12L12 17L22 12" stroke="#8B5CF6" strokeWidth="1.5"/></svg>
                     <span className="text-base font-bold text-white">Echidna Foundry</span>
                 </div>
            </div>
            {/* Navigation */}
            <div className="flex-grow py-2">
                <nav className="space-y-0.5 px-2 mt-3">
                    <SidebarNavItem
                        key="dashboard" icon={LayoutDashboard} text="Dashboard"
                        href="/echidna/efoundry/dashboard" active={currentPath === '/echidna/efoundry/dashboard'}
                        onClick={(e) => handleNavigate(e, '/echidna/efoundry/dashboard')}
                    />
                    <SidebarNavItem
                        key="assistants" icon={Bot} text="My Assistants"
                        href="/echidna/myassistants" active={currentPath === '/echidna/myassistants'}
                        onClick={(e) => handleNavigate(e, '/echidna/myassistants')}
                    />
                    <SidebarNavItem
                        key="stats" icon={BarChart3} text="Analytics"
                        href="/echidna/stats" active={currentPath.startsWith('/echidna/stats')} // Active if path starts with /stats
                        onClick={(e) => handleNavigate(e, '/echidna/stats')}
                    />
                    {/* Add the Agent Analysis link */}
                    <SidebarNavItem
                        key="analysis" icon={Activity} text="Agent Analysis"
                        href="/echidna/analysis" active={currentPath === '/echidna/analysis'} // Mark this page as active
                        onClick={(e) => handleNavigate(e, '/echidna/analysis')}
                    />
                </nav>
            </div>
            {/* User Info */}
            <div className="p-2 border-t border-gray-800/50 mt-auto">
                <ClickableLink href="#profile" title={userEmail} className="flex items-center p-2 rounded-md text-xs text-gray-400 hover:bg-gray-800/50 hover:text-white group w-full">
                    <Mail className="w-4 h-4 mr-2 text-gray-500 group-hover:text-gray-300 flex-shrink-0" strokeWidth={1.5} />
                    <span className="truncate font-medium">{userEmail}</span>
                </ClickableLink>
            </div>
        </div>
    );
};

// --- Top Bar ---
const TopBar = ({ pageTitle }) => {
    const { user } = useUser();
    const userInitial = user?.firstName?.[0] || user?.username?.[0] || user?.primaryEmailAddress?.emailAddress?.[0] || '?';
    const userDisplayName = user?.firstName ? `${user.firstName}${user.lastName ? ' ' + user.lastName[0] + '.' : ''}` : user?.username || user?.primaryEmailAddress?.emailAddress || 'Account';

    return (
        <div className="h-14 bg-gray-950 border-b border-gray-800/50 flex items-center justify-between px-4 flex-shrink-0 sticky top-0 z-20">
             <div className="flex items-center space-x-3"><ClickableDiv className="p-1.5 -m-1.5 rounded hover:bg-gray-800/50 text-gray-300 hover:text-white" title="Services Menu"><Menu className="w-5 h-5" strokeWidth={1.5}/></ClickableDiv><span className="text-sm font-medium text-white">{pageTitle}</span></div>
             <div className="flex items-center space-x-3 md:space-x-4"><div className="relative hidden md:block"><Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" strokeWidth={1.5}/><input type="search" placeholder={`Search ${pageTitle}...`} className="bg-gray-800/60 border border-gray-700/50 rounded-md pl-8 pr-2 py-1 w-48 lg:w-64 text-xs text-gray-200 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 focus:outline-none focus:bg-gray-800" /></div><ClickableDiv className="flex items-center text-xs text-gray-400 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50" title="Region"><Globe className="w-4 h-4 mr-1" strokeWidth={1.5}/><span>us-east-1</span></ClickableDiv><ClickableLink href="#help" title="Support" className="text-gray-400 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50"><LifeBuoy className="w-5 h-5" strokeWidth={1.5}/></ClickableLink><ClickableDiv title="Notifications" className="text-gray-400 hover:text-white relative p-1.5 -m-1.5 rounded hover:bg-gray-800/50"><Bell className="w-5 h-5" strokeWidth={1.5}/></ClickableDiv>
             <ClickableDiv title="Account" className="flex items-center text-xs text-gray-300 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50">
                 <span className="hidden md:inline mr-1 truncate max-w-[100px]" title={userDisplayName}>{userDisplayName}</span>
                 <span className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">{userInitial.toUpperCase()}</span>
                 <ChevronDown className="w-4 h-4 ml-1 text-gray-500" strokeWidth={1.5}/>
             </ClickableDiv>
             </div>
         </div>
    );
};

// --- Agent Analysis Content Component ---
const AgentAnalysisContent = () => {
  const { user } = useUser();
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [agentError, setAgentError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null); // Specific error for analysis

  // Animation Hooks
  const [headerRef, isHeaderVisible] = useOnScreen();
  const [agentSelectRef, isAgentSelectVisible] = useOnScreen();
  const [analysisAreaRef, isAnalysisAreaVisible] = useOnScreen();


  // Fetch Agents
  const fetchAgents = useCallback(async () => {
    if (!user?.id) return;
    setLoadingAgents(true);
    setAgentError(null);
    setAgents([]);
    setSelectedAgent(null);
    setAnalysis(null); // Clear analysis when fetching agents
    setAnalysisError(null);
    try {
        // Using Axios for consistency
      const response = await axios.post('https://web-production-5ee8.up.railway.app/echidna/myagents', { cid: user.id });
      // const response = await axios.post('http://localhost:5001/echidna/myagents', { cid: user.id }); // Local

      if (response.data.success) {
        setAgents(response.data.agents || []);
        // Auto-select the first agent if available
        if (response.data.agents && response.data.agents.length > 0) {
          setSelectedAgent(response.data.agents[0]);
        }
      } else {
        setAgentError(response.data.error || 'Failed to fetch agents');
      }
    } catch (err) {
      setAgentError(err.response?.data?.message || err.message || 'Network error: Unable to fetch agents');
      console.error("Fetch Agents Error:", err);
    } finally {
      setLoadingAgents(false);
    }
  }, [user]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);


  // Fetch Analysis
  const fetchAnalysis = useCallback(async () => {
    if (!selectedAgent || !user?.id) return;

    setAnalysisLoading(true);
    setAnalysisError(null); // Clear previous analysis error
    setAnalysis(null); // Clear previous analysis results

    try {
        // Using Axios
      const response = await axios.post('https://web-production-5ee8.up.railway.app/echidna/analys', {
        agentId: selectedAgent._id,
        cid: user.id
      });
      // const response = await axios.post('http://localhost:5001/echidna/analys', { // Local
      //   agentId: selectedAgent._id,
      //   cid: user.id
      // });


      if (response.data.success) {
        setAnalysis(response.data.data); // Access data.data as per original code
      } else {
        setAnalysisError(response.data.error || 'Failed to fetch analysis');
      }
    } catch (err) {
      setAnalysisError(err.response?.data?.message || err.message || 'Network error: Unable to fetch analysis');
      console.error("Fetch Analysis Error:", err);
    } finally {
      setAnalysisLoading(false);
    }
  }, [selectedAgent, user]);


  // Download Analysis Text File
  const downloadAnalysisText = () => {
    if (!analysis || !selectedAgent) return;

    // Simple text formatting
    const textContent = `
Agent Analysis Report
=======================

Agent Name: ${selectedAgent.agentName}
Agent ID: ${selectedAgent._id}
Generated On: ${new Date().toLocaleString()}

Statistics
----------
Message Count: ${analysis.statistics?.messageCount ?? 'N/A'}
Unique Users: ${analysis.statistics?.uniqueUserCount ?? 'N/A'}
Last Updated: ${new Date(selectedAgent.updatedAt).toLocaleString()}

Analysis Summary
----------------
${analysis.analysis || 'No analysis content available.'}
`;

    const blob = new Blob([textContent.trim()], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Sanitize filename slightly
    const filename = `${selectedAgent.agentName.replace(/[^a-z0-9]/gi, '_')}-analysis.txt`;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };


  // --- Render Logic ---

  // Initial Loading state for Agents
  if (loadingAgents) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)] w-full"> {/* Adjust height */}
        <div className="text-center text-gray-500">
          <Loader2 className="animate-spin h-8 w-8 mx-auto text-purple-500 mb-3" />
          <p className="text-sm">Loading agent data...</p>
        </div>
      </div>
    );
  }

  // Error state for Agents
  if (agentError) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)] w-full">
        <div className="max-w-md p-6 border border-red-700/50 bg-gray-950 rounded-lg text-center">
            <ShieldAlert className="w-10 h-10 text-red-500 mx-auto mb-4"/>
          <h2 className="text-lg font-semibold text-red-400 mb-2">Error Loading Agents</h2>
          <p className="text-sm text-gray-400 mb-5">{agentError}</p>
          <ClickableDiv
            onClick={fetchAgents}
            className="px-4 py-1.5 text-sm border border-gray-600 hover:bg-gray-800 transition-colors text-gray-300 rounded-md"
          >
            Try again
          </ClickableDiv>
        </div>
      </div>
    );
  }

  // Main Content
  return (
    <div className="p-6 lg:p-8 h-full">
        {/* Header */}
        <header
            ref={headerRef}
            className={`flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4 transition-all duration-500 ease-out ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
        >
            <div>
                <div className="flex items-center gap-3 mb-1">
                    <Activity className="w-7 h-7 text-purple-500 flex-shrink-0" strokeWidth={1.5} />
                    <h1 className="text-3xl font-bold tracking-tight text-gray-100">Agent Analysis</h1>
                </div>
                <p className="text-base text-gray-400 sm:ml-10">Generate and view interaction analysis for your assistants.</p>
            </div>
        </header>

      {agents.length === 0 ? (
         <div className="text-center py-16 px-6 bg-gray-950 border border-dashed border-gray-800/50 rounded-lg">
             <Info className="w-12 h-12 text-gray-700 mx-auto mb-4" strokeWidth={1}/>
             <p className="text-gray-500">No assistants found.</p>
             <p className="text-xs text-gray-600 mt-1">Create an assistant on the 'My Assistants' page first.</p>
         </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Agent Selection Column */}
          <section
              ref={agentSelectRef}
              className={`lg:col-span-1 transition-all duration-500 ease-out delay-100 ${isAgentSelectVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
          >
            <h2 className="text-lg font-semibold text-gray-300 mb-4 border-b border-gray-800/50 pb-2">
                Select Assistant
            </h2>
            <div className="space-y-3 max-h-[calc(100vh-20rem)] overflow-y-auto scrollbar-thin pr-1"> {/* Scrollable agent list */}
              {agents.map(agent => (
                <ClickableDiv
                  key={agent._id}
                  onClick={() => {
                    if (selectedAgent?._id !== agent._id) { // Only update if different agent selected
                        setSelectedAgent(agent);
                        setAnalysis(null); // Clear previous analysis
                        setAnalysisError(null); // Clear previous error
                    }
                  }}
                   className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ease-out w-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black ${
                     selectedAgent?._id === agent._id
                       ? 'border-purple-500 bg-gray-900 shadow-md ring-1 ring-purple-500/80'
                       : 'border-gray-800/70 bg-gray-950 hover:border-gray-700 hover:bg-gray-900/70'
                   }`}
                  title={`Select ${agent.agentName}`}
                >
                   <div className="flex justify-between items-center w-full">
                        <div className="flex-1 overflow-hidden mr-2">
                           <h3 className={`text-sm font-semibold truncate ${selectedAgent?._id === agent._id ? 'text-purple-300' : 'text-gray-200'}`}>
                               {agent.agentName}
                           </h3>
                           <p className="text-xs text-gray-500 mt-0.5">
                               Created: {new Date(agent.createdAt).toLocaleDateString()}
                           </p>
                        </div>
                        {selectedAgent?._id === agent._id && (
                           <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0 ml-auto" />
                        )}
                   </div>
                </ClickableDiv>
              ))}
            </div>
          </section>

          {/* Analysis Display Column */}
          <section
              ref={analysisAreaRef}
              className={`lg:col-span-2 transition-all duration-500 ease-out delay-200 ${isAnalysisAreaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
          >
            {!selectedAgent ? (
                 <div className="flex items-center justify-center h-full border border-dashed border-gray-800/50 rounded-lg bg-gray-950 p-10 text-center">
                    <p className="text-gray-500">Select an assistant from the list to view or generate analysis.</p>
                 </div>
            ) : (
              <div className="space-y-6">
                {/* Agent Info Header and Generate Button */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-gray-800/50">
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-gray-100">{selectedAgent.agentName}</h2>
                    <p className="text-xs text-gray-500 mt-1 font-mono">
                      ID: {selectedAgent._id}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Last Updated: {new Date(selectedAgent.updatedAt).toLocaleString()}
                    </p>
                  </div>

                  {/* Only show Generate button if no analysis exists or error occurred */}
                  {(!analysis || analysisError) && (
                    <ClickableDiv
                      onClick={fetchAnalysis}
                      disabled={analysisLoading}
                      className={`px-4 py-2 text-sm font-semibold rounded-lg shadow-md transition-all flex items-center gap-2 flex-shrink-0 ${
                        analysisLoading
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : 'bg-purple-600 text-white hover:bg-purple-700 shadow-purple-600/20'
                      }`}
                    >
                      {analysisLoading ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</>
                      ) : (
                        <><Activity className="w-4 h-4" strokeWidth={2}/> Generate Analysis</>
                      )}
                    </ClickableDiv>
                  )}
                </div>

                {/* Analysis Loading State */}
                {analysisLoading && (
                    <div className="flex justify-center items-center h-40 text-gray-500">
                        <Loader2 className="w-6 h-6 mr-2 animate-spin text-purple-500" /> Loading analysis...
                    </div>
                )}

                {/* Analysis Error State */}
                {!analysisLoading && analysisError && (
                     <div className="p-4 border border-red-700/50 bg-gray-950/50 rounded-lg">
                        <p className="text-sm text-red-400 flex items-center gap-2">
                            <ShieldAlert className="w-5 h-5 flex-shrink-0" />
                            <span>Error: {analysisError}</span>
                        </p>
                    </div>
                )}

                {/* Display Analysis */}
                {!analysisLoading && !analysisError && analysis && (
                  <div className="space-y-6">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-950 rounded-lg border border-gray-800/50">
                        <div className="flex items-center text-gray-400 mb-2">
                          <BarChart3 className="w-4 h-4 mr-2" strokeWidth={1.5}/>
                          <h3 className="text-xs uppercase tracking-wider font-medium">Message Count</h3>
                        </div>
                        <p className="text-2xl font-semibold text-gray-100">{analysis.statistics?.messageCount ?? 'N/A'}</p>
                      </div>

                      <div className="p-4 bg-gray-950 rounded-lg border border-gray-800/50">
                        <div className="flex items-center text-gray-400 mb-2">
                          <Users className="w-4 h-4 mr-2" strokeWidth={1.5}/>
                          <h3 className="text-xs uppercase tracking-wider font-medium">Unique Users</h3>
                        </div>
                        <p className="text-2xl font-semibold text-gray-100">{analysis.statistics?.uniqueUserCount ?? 'N/A'}</p>
                      </div>
                    </div>

                    {/* Analysis Text Box */}
                    <div className="p-5 bg-gray-950 rounded-lg border border-gray-800/50">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-200">Analysis Summary</h3>
                        <ClickableDiv
                          onClick={downloadAnalysisText}
                          title="Download as text file"
                          className="text-xs font-medium text-gray-400 hover:text-purple-400 flex items-center gap-1 p-1 -m-1 rounded"
                        >
                          <Download className="w-3.5 h-3.5" /> Download
                        </ClickableDiv>
                      </div>
                      <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-line max-h-96 overflow-y-auto scrollbar-thin">
                        {analysis.analysis || 'No analysis content available.'}
                      </div>
                    </div>

                    {/* Regenerate Button */}
                    <div className="pt-4 border-t border-gray-800/50">
                        <ClickableDiv
                            onClick={fetchAnalysis} // Re-use fetchAnalysis
                            disabled={analysisLoading}
                            className={`px-4 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5 transition-colors ${
                                analysisLoading
                                ? 'border-gray-700 bg-gray-800 text-gray-500 cursor-not-allowed'
                                : 'border border-gray-700 hover:bg-gray-800 text-gray-300'
                            }`}
                            >
                                {analysisLoading ? (
                                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Regenerating...</>
                                ) : (
                                    <><RefreshCw className="w-3.5 h-3.5" /> Regenerate Analysis</>
                                )}
                        </ClickableDiv>
                    </div>
                  </div>
                )}

                 {/* Placeholder when no analysis loaded yet */}
                 {!analysisLoading && !analysisError && !analysis && (
                    <div className="flex flex-col items-center justify-center h-60 border border-dashed border-gray-800/50 rounded-lg bg-gray-950 p-10 text-center">
                        <Activity className="w-10 h-10 text-gray-700 mb-4" strokeWidth={1}/>
                        <h3 className="text-lg font-semibold text-gray-400 mb-2">Analysis Not Generated</h3>
                        <p className="text-sm text-gray-500 max-w-xs mx-auto">Click "Generate Analysis" to process the interactions for this assistant.</p>
                    </div>
                 )}

              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};


// --- Main Page Component (Shell) ---
function AgentAna() {
    const pageTitle = 'Agent Analysis';

    return (
        <div className="flex h-screen bg-black font-sans antialiased text-gray-200">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <TopBar pageTitle={pageTitle} />
                <main className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black relative bg-black">
                    <div className="relative z-10 h-full">
                        <AgentAnalysisContent />
                    </div>
                </main>
            </div>
            <style jsx global>{`
                /* Echtools global styles */
                .scrollbar-thin { scrollbar-width: thin; scrollbar-color: theme(colors.gray.700 / 0.5) transparent; }
                .scrollbar-thin::-webkit-scrollbar { width: 5px; height: 5px; }
                .scrollbar-thin::-webkit-scrollbar-thumb { background-color: theme(colors.gray.700 / 0.5); border-radius: 3px; }
                .scrollbar-thin::-webkit-scrollbar-track { background-color: transparent; }
                body { background-color: black; margin: 0; }
                @keyframes bounce-quick { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
                .animate-bounce-quick { animation: bounce-quick 0.3s ease-in-out; }
                @keyframes fade-in-fast { from { opacity: 0; } to { opacity: 1; } }
                .animate-fade-in-fast { animation: fade-in-fast 0.2s ease-out forwards; }
                @keyframes spin { to { transform: rotate(360deg); } }
                .animate-spin { animation: spin 1s linear infinite; }
                .line-clamp-2 { overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
            `}</style>
        </div>
    );
}

export default AgentAna;